/**
 * /verify-sign-in — email-code verification for Clerk's
 * `needs_client_trust` and `needs_first_factor` statuses.
 *
 * Two entry paths:
 *   1. Password sign-in on a new / untrusted device without MFA
 *      (`needs_client_trust`, Clerk Core 3). The account's primary
 *      email gets a 6-digit code and the user pastes it here.
 *   2. Instance requires an email-code first factor on sign-in
 *      (`needs_first_factor` with an `email_code` supported strategy).
 *
 * Both paths call `attemptFirstFactor({ strategy: 'email_code' })`.
 *
 * Not `/two-factor`: the two-factor route guards on
 * `signIn.status === 'needs_second_factor'` and uses
 * `attemptSecondFactor`. Client-trust + first-factor verification
 * are first-factor flows (same code path as password reset's
 * email-code gate).
 *
 * On success the status usually flips to `complete`; MFA-enabled
 * instances may then require `needs_second_factor`. We forward to
 * `/two-factor` in that branch so the existing TOTP / backup-code
 * UI picks up.
 */

import { Button } from '@/components/ui/button';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import {
  activateSession,
  attemptClientTrustEmailCode,
  prepareClientTrustEmailCode,
} from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';

const RESEND_COOLDOWN_SECONDS = 30;

export const Route = createFileRoute('/_auth/verify-sign-in')({
  component: VerifySignInPage,
  validateSearch: (search): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

function VerifySignInPage(): ReactElement {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendNotice, setResendNotice] = useState<string | undefined>(undefined);

  // Guard against React 19 strict-mode double-submits when the OTP
  // field auto-completes on the final digit.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  async function attempt(value: string) {
    if (!isLoaded || !signIn || !setActive) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(undefined);
    setCodeError(undefined);

    const result = await attemptClientTrustEmailCode(signIn, value);

    if (result.kind === 'error') {
      setCodeError(result.fieldErrors.code);
      setFormError(result.fieldErrors.code ? undefined : result.formError);
      setCode('');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (result.kind === 'complete') {
      await activateSession({
        setActive,
        sessionId: result.sessionId,
        redirectUrl: redirect ?? '/',
      });
      return;
    }

    if (result.kind === 'needs_second_factor') {
      navigate({ to: '/two-factor', search: { flow: 'sign-in', redirect } });
      return;
    }

    // `needs_new_password` / `needs_identifier` / `needs_client_trust` /
    // `needs_first_factor` after an `attemptFirstFactor` indicates
    // something unusual (e.g. the attempt re-issued the same state).
    // Surface a generic "start over" rather than looping.
    setFormError('Verification is in an unexpected state. Start sign-in again.');
    submittingRef.current = false;
    setSubmitting(false);
  }

  function onChange(value: string) {
    setCode(value);
    if (value.length === 6) {
      void attempt(value);
    }
  }

  async function onResend() {
    if (!isLoaded || !signIn) return;
    setResendNotice(undefined);
    setFormError(undefined);
    const result = await prepareClientTrustEmailCode(signIn);
    if (result.kind === 'error') {
      setFormError(result.formError ?? 'Unable to resend code. Try again.');
      return;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setResendNotice('New code sent.');
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-6 text-sm text-[var(--color-mute)]">
        Loading…
      </div>
    );
  }

  if (
    !signIn ||
    (signIn.status !== 'needs_client_trust' && signIn.status !== 'needs_first_factor')
  ) {
    return (
      <div className="space-y-3 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">No sign-in in progress</h1>
        <p className="text-sm text-[var(--color-mute)]">Start again from the sign-in page.</p>
        <Button onClick={() => navigate({ to: '/sign-in', search: { redirect } })}>Sign in</Button>
      </div>
    );
  }

  const emailFactor = (signIn.supportedFirstFactors ?? []).find((f) => f.strategy === 'email_code');
  const safeIdentifier =
    emailFactor && 'safeIdentifier' in emailFactor
      ? (emailFactor.safeIdentifier as string | undefined)
      : undefined;

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Verify it's you</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Enter the 6-digit code we sent to{' '}
          <span className="font-medium text-[var(--color-ink)]">
            {safeIdentifier ?? 'your email'}
          </span>
          . This only happens on new or untrusted devices.
        </p>
      </header>
      <div className="flex flex-col items-center gap-3">
        <OTPField length={6} value={code} onValueChange={onChange} disabled={submitting}>
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
            <OTPFieldInput key={i} />
          ))}
        </OTPField>
        {codeError && (
          <p className="text-xs text-[var(--color-danger)]" role="alert">
            {codeError}
          </p>
        )}
      </div>
      {formError && (
        <div
          className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {formError}
        </div>
      )}
      <div className="flex flex-col items-center gap-2 text-sm">
        <button
          type="button"
          onClick={onResend}
          disabled={cooldown > 0}
          className="font-medium text-[var(--color-accent-brand)] hover:underline disabled:pointer-events-none disabled:text-[var(--color-mute)]"
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
        {resendNotice && <p className="text-xs text-[var(--color-mute)]">{resendNotice}</p>}
      </div>
    </div>
  );
}
